import React, { Dispatch, SetStateAction, useState } from "react";
import { Button, Collapse, Form } from "react-bootstrap";
import { ActionHandlerFunction, AdminUser, CollectionServerUser, IActionHandler, OrgInfo } from "@preveil-api";
import {
  Account, AdminErrorMessages, AdminSuccessMessages, AdminUIActionTypes, CSV_COLUMNS, GlobalErrorMessages, Message, MessageAnchors, MessageHandlerDisplayType, OrgUserRoles,
  RegexHelper, useAddOrganizationMembersMutation, useAppDispatch, useCsvDataValidation, useOrgSubsumeMemberMutation, usePostUsersFindMutation, UserInviteMap, UserValidationState, FileTemplates, FileUploadTypes,
  AccountSelectionTypes, AccountSelectionType
} from "src/common";
import { DraggableContainer, Icon, InfoPopover } from "src/components";
import { CSVInvitesDisplay } from "..";
import { uiActions } from "src/store";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  org_user_ids: string[];
  org_info: OrgInfo;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  handleAction: ActionHandlerFunction;
}

function AddMultipleUsersComponent(props: AllProps) {
  const { current_account, org_user_ids, org_info, handleAction, setShowModal } = props;
  const dispatch = useAppDispatch();
  const [csv_data_hash, setCsvDataHash] = useState<UserInviteMap>({});
  const [open_collapse, setOpenCollapse] = useState<boolean>(false);
  const [subsume_accounts_map, setSubsumeAccountsMap] = useState<Map<string, CollectionServerUser>>();
  const [findUsers] = usePostUsersFindMutation();
  const [addOrganizationMembers] = useAddOrganizationMembersMutation();
  const [orgSubsumeMember] = useOrgSubsumeMemberMutation();
  const { valid_addresses, invalid_addresses, duplicate_addresses, is_data_drop, handleValidateData, resetData } = useCsvDataValidation(org_user_ids);
  const { validSubsumeAccountInvites, validExternalAccountInvites } = separateSubsumeAndExternalAccounts(valid_addresses);

  const AddMultipleUsersRequests = {
    handleCsvData: (csv_file: any) => {
      handleCsvFileData(csv_file);
    },
    handleReset: () => {
      resetData();
    },
    handlePageError: (params: { message: string, stack?: any }) => {
      const { message, stack } = params;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.toastr), stack));
    }
  };

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((AddMultipleUsersRequests as any)[callback] instanceof Function) {
      (AddMultipleUsersRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      AddMultipleUsersRequests.handlePageError({ message, stack: action_obj });
    }
  }

  function submitCSVData() {
    // Note: we could probably saved in state the validSubsumeAccountInvites and validExternalAccountInvites
    // and there would be no need to iterate over the csv_data_hash to get the user data again.
    // but this is probably easier and less costly than updating state every time invites are changed or removed.
    const external_accounts_collection: AdminUser[] = [];
    const subsume_accounts_collection: AdminUser[] = [];

    valid_addresses.forEach((address: string) => {
      const { first_name, last_name, email, department, account_type } = csv_data_hash[address];
      const user_invite = { display_name: `${first_name} ${last_name}`, user_id: email, department, account_type, devices: [], status: "pending", role: OrgUserRoles.standard };
      if (subsume_accounts_map?.has(email)) {
        subsume_accounts_collection.push(user_invite);
      } else {
        external_accounts_collection.push(user_invite);
      }
    });

    sendInvitesFromCsv(subsume_accounts_collection, external_accounts_collection);
  }

  // Description: Handles invites request from CSV file data, for both subsume and external accounts.
  async function sendInvitesFromCsv(subsume_accounts: AdminUser[], external_accounts: AdminUser[]) {
    if (subsume_accounts.length >= 1 || external_accounts.length >= 1) {
      try {
        // if there are any external accounts in the csv file, we need to call a different API.
        if (external_accounts.length >= 1 && subsume_accounts.length >= 1) {
          await sendUsersInvite(external_accounts);
        } else if (external_accounts.length >= 1 && subsume_accounts.length === 0) {
          await sendUsersInvite(external_accounts).then(() => {
            const success_message = AdminSuccessMessages.success_invite_user.replace(MessageAnchors.message_content, `${external_accounts.length} users`);
            dispatch(uiActions.handleSetMessage(new Message(success_message)));
            reset();
          });
        }

        if (subsume_accounts.length >= 1) {
          await Promise.all(subsume_accounts.map((subsume_account) => sendSubsumeInvite(subsume_account))).then(() => {
            const total_invites = subsume_accounts.length + external_accounts.length;
            const success_message = AdminSuccessMessages.success_invite_user.replace(MessageAnchors.message_content, `${total_invites} users`);
            reset();
            dispatch(uiActions.handleSetMessage(new Message(success_message)));
          });
        }

      } catch (error) {
        const error_message = AdminErrorMessages.error_invite_request_csv_users;
        dispatch(uiActions.handleRequestErrors(new Message(error_message, MessageHandlerDisplayType.toastr), error));
      }
    }
  }

  function reset() {
    resetData();
    handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
  }

  // Description: API for sending invites to external users.
  async function sendUsersInvite(users: AdminUser[]) {
    await addOrganizationMembers({
      orgId: org_info.org_id,
      userId: current_account.user_id,
      users
    })
      .unwrap()
      .catch((error) => {
        const error_message = AdminErrorMessages.error_invite_user_to_org.replace(MessageAnchors.user_id, `${users.length === 1 ? users[0].display_name : `${users.length} users`}`);
        dispatch(uiActions.handleRequestErrors(new Message(error_message, MessageHandlerDisplayType.toastr), error));
      });
  }

  async function sendSubsumeInvite(user: AdminUser) {
    await orgSubsumeMember({
      orgId: org_info.org_id,
      userId: current_account.user_id,
      subsumeUserId: user.user_id,
      department: user.department
    })
      .unwrap()
      .catch((error) => {
        const error_message = AdminErrorMessages.error_invite_user_to_org.replace(MessageAnchors.user_id, `${user.user_id}`);
        dispatch(uiActions.handleRequestErrors(new Message(error_message, MessageHandlerDisplayType.toastr), error));
      });
  }

  // Description: After the CSV data is validated, we will split the valid addresses into subsume and external accounts.
  function separateSubsumeAndExternalAccounts(user_invites: string[]) {
    if (user_invites.length === 0) return { validSubsumeAccountInvites: [], validExternalAccountInvites: [] };
    // We are partitioning the valid addresses into subsume and external accounts.
    const [subsume_collection, collection] = _.partition(user_invites, (address: string) => subsume_accounts_map?.has(address));
    return {
      validSubsumeAccountInvites: subsume_collection,
      validExternalAccountInvites: collection
    };
  }

  // Description: Handling the CSV data after it's been parsed.
  async function handleCsvFileData(csv_file: any) {
    if (csv_file?.errors && csv_file?.errors.length >= 1) {
      // Papaparse returns some errors when the CSV formatting is not correct
      // or the data is missing a delimiter in each row, but that doesn't mean the data will not be parse.
      // so we are just logging those errors.
      const message = AdminErrorMessages.error_csv_upload_format;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), csv_file.errors));
    }
    const { data, meta } = csv_file;
    const { fields } = meta;
    const is_valid_columns = fields.length === [...Object.values(CSV_COLUMNS)].length && fields.every((field: "First Name" | "Last Name" | "Email Address" | "Department") => ([...Object.values(CSV_COLUMNS)]).includes(field));
    if (!is_valid_columns || data.length === 0) {
      if (data.length === 0) {
        const message = AdminErrorMessages.error_csv_empty_file;
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), data));
      } else if (!is_valid_columns) {
        const message = AdminErrorMessages.error_csv_wrong_heading;
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), meta.fields));
      }
    } else {
      // NOTE: We only need the email address/user_id to validate each row data of the CSV.
      // And also keeping a reference (inside a ref) of the csv data to be used later.
      const user_addresses_from_csv: string[] = [];
      const csv_data_hash: UserInviteMap = {};
      const seen_emails = new Set<string>();
      data.forEach((row: any, index: string) => {
        const address = row[CSV_COLUMNS.email] || "";
        const department = row[CSV_COLUMNS.department] || "";
        const first_name = row[CSV_COLUMNS.first_name] || "";
        const last_name = row[CSV_COLUMNS.last_name] || "";
        // NOTE: Default all accounts to "full" - can be changed via bulk selector or per-user UI
        // NOTE: We'll consider a row valid, if the email and department are present, at least one of first-name and last-name is present,
        // and the name does not contain forbidden characters (<, >, @, emoji) or exceed 256 characters combined.
        const has_valid_name = (!!first_name || !!last_name) &&
          RegexHelper.testDisplayNameChars(first_name) &&
          RegexHelper.testDisplayNameChars(last_name) &&
          `${first_name.trim()} ${last_name.trim()}`.length < 256;
        const is_valid_row = !!address && !!department && has_valid_name;
        // NOTE: We are using the email as the id for the csv data hash, if the row is not valid we'll generate a unique id.
        // This is to keep the reference of the csv data to be used later.
        // All the invalid rows will fail the validation inside useCsvDataValidation and will be stored in the invalid_addresses array.
        const id = is_valid_row ? address : _.uniqueId(`row_${index}`);

        // NOTE: Skip duplicate emails within the CSV itself - only keep the first occurrence
        if (is_valid_row && seen_emails.has(address.toLowerCase())) {
          return;
        }
        is_valid_row && seen_emails.add(address.toLowerCase());
        user_addresses_from_csv.push(id);
        csv_data_hash[id] = { first_name, last_name, email: address, department, account_type: "full" };
      });

      // NOTE: keeping the reference of the csv data in state since we are going to pass it down to other components.
      setCsvDataHash(csv_data_hash);
      try {
        // Find preveil accounts from the csv data.
        await findCsvUsers(user_addresses_from_csv);
      } catch (error) {
        const message = AdminErrorMessages.error_finding_user_accounts_from_csv;
        dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), error));
      }
      handleValidateData(user_addresses_from_csv);
    }
  };

  // Description: After getting the response from getUser
  // if there are any subsume accounts, we will add them to the local_users_accounts_map.
  async function findCsvUsers(addresses_from_csv: string[]) {
    const result = await getUser(addresses_from_csv);
    if (!!result) {
      const { users } = result;
      if (!!users && users.length > 0) {
        const subsume_accounts_map = new Map<string, CollectionServerUser>();
        users.forEach((user: CollectionServerUser) => {
          const { user_id } = user;
          subsume_accounts_map.set(user_id, user);
        });
        setSubsumeAccountsMap(subsume_accounts_map);
      }
    }
  };

  // Description: Get the user data from the API.
  // We are using the findUsers API to see if any of the users has a preveil account.
  async function getUser(user_ids: string[]) {
    if (current_account && user_ids.length > 0) {
      const users = user_ids.map((user_id) => {
        return { user_id, key_version: -1 };
      });
      return await findUsers({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: { spec: users }
      })
        .unwrap()
        .then((response) => {
          const { users, errors } = response;
          return { users, errors };
        })
        .catch((error) => {
          const message = AdminErrorMessages.error_finding_user_accounts_from_csv;
          dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), error));
        });
    }
  }

  // Description: Handling the delete action for the csv addresses
  function handleDeleteAction(id?: string) {
    if (!!id) {
      const data = [...valid_addresses, ...invalid_addresses, ...duplicate_addresses].filter((address: string) => address !== id);
      handleValidateData(data);
    }
  }

  // Description: Compute the current bulk selection state based on all users' account types
  // Returns the account type if all users have the same type, or "custom" if mixed
  // NOTE: All account types from CSV are now defaulted to "full" during parsing
  function getBulkAccountType(): AccountSelectionType | undefined {
    if (validExternalAccountInvites.length === 0) return undefined;
    const first_type = csv_data_hash[validExternalAccountInvites[0]]?.account_type;
    // Check if all have the same type
    const all_same = validExternalAccountInvites.every((address: string) => csv_data_hash[address]?.account_type === first_type);
    return all_same ? (first_type as AccountSelectionType) : AccountSelectionTypes.custom;
  }

  // Description: Handle bulk account type change - sets all valid users to the selected type
  // NOTE: "custom" is a display-only state and should not modify account types
  function handleBulkAccountTypeChange(type: AccountSelectionType) {
    if (type === AccountSelectionTypes.custom) return; // Don't modify on custom selection
    const updated_hash = { ...csv_data_hash };
    validExternalAccountInvites.forEach((address: string) => {
      if (updated_hash[address]) {
        updated_hash[address] = { ...updated_hash[address], account_type: type };
      }
    });
    setCsvDataHash(updated_hash);
  }

  // Description: Handle individual account type change for a specific user
  function handleIndividualAccountTypeChange(user_id: string, type: AccountSelectionType) {
    if (type === AccountSelectionTypes.custom) return; // Custom is not a valid individual selection
    const updated_hash = { ...csv_data_hash };
    if (updated_hash[user_id]) {
      updated_hash[user_id] = { ...updated_hash[user_id], account_type: type };
    }
    setCsvDataHash(updated_hash);
  }

  return <>
    {!is_data_drop ? <>
      <p>
        Download and use the upload template to accurately add multiple users at once
        <a
          href={FileTemplates.multiple_users}
          target="_blank"
          rel="noreferrer">
          <Icon className="ficon-download small" />
          Preveil-User-Upload-Template.csv
        </a>
      </p>
      <DraggableContainer handleAction={handlePageActions} file_upload_type={FileUploadTypes.CSV} />
    </> : <div className="data-display-section">
      {validSubsumeAccountInvites.length > 0 &&
        <CSVInvitesDisplay
          csv_data_hash={csv_data_hash}
          validation_state={UserValidationState.valid_subsume}
          collection={validSubsumeAccountInvites}
          deleteFunction={handleDeleteAction}
          onAccountTypeChange={handleIndividualAccountTypeChange}
        />
      }
      <Form className="horizontal-radio-group">
        <Form.Label>
          Select Account Type
          <InfoPopover message={"Select the account type to apply to all new users below. Individual users can be changed in the list."} placement="bottom-end" />
        </Form.Label>
        {_.map(AccountSelectionTypes, (_type: AccountSelectionType) => {
          const bulk_type = getBulkAccountType();
          return <Form.Check
            key={`account_type_${_type}`}
            inline
            type="radio"
            id={`account_type_${_type}`}
            name="account_type"
            label={_type}
            checked={bulk_type === _type}
            disabled={_type === AccountSelectionTypes.custom}
            onChange={() => handleBulkAccountTypeChange(_type)} />;
        })}
      </Form>
      {validExternalAccountInvites.length > 0 &&
        <CSVInvitesDisplay
          csv_data_hash={csv_data_hash}
          validation_state={UserValidationState.valid_external}
          collection={validExternalAccountInvites}
          deleteFunction={handleDeleteAction}
          onAccountTypeChange={handleIndividualAccountTypeChange}
        />
      }
      {
        (invalid_addresses.length > 0 || duplicate_addresses.length > 0) &&
        <div className={`invalid-card ${(validSubsumeAccountInvites.length > 0 || validExternalAccountInvites.length > 0) ? "show-divider-line" : ""}`}>
          <header>
            <h3 className="mb-2">
              Invites that will <i className="emphasis">not</i> be sent
            </h3>
            <Button variant="icon" onClick={() => setOpenCollapse(!open_collapse)} aria-controls="collapse-invalid-area">
              <Icon className={`${open_collapse ? "ficon-chevron-up" : "ficon-chevron-down"}`} />
            </Button>
          </header>
          <Collapse in={open_collapse}>
            <div id="collapse-invalid-area">
              <div className="pt-2 pb-0">
                {duplicate_addresses.length >= 1 &&
                  <CSVInvitesDisplay
                    csv_data_hash={csv_data_hash}
                    validation_state={UserValidationState.duplicate}
                    collection={duplicate_addresses}
                    deleteFunction={handleDeleteAction}
                  />
                }
                {invalid_addresses.length >= 1 &&
                  <CSVInvitesDisplay
                    csv_data_hash={csv_data_hash}
                    validation_state={UserValidationState.invalid}
                    collection={invalid_addresses}
                    deleteFunction={handleDeleteAction}
                  />
                }
              </div>
            </div>
          </Collapse>
        </div>
      }
    </div>
    }
    <div className="btn-panel mt-4">
      <Button onClick={submitCSVData} disabled={valid_addresses.length === 0}>Add/Create Accounts</Button>
      <Button variant="no-outline-primary" onClick={() => setShowModal(false)}>Cancel</Button>
    </div>
  </>;
}

export default React.memo(AddMultipleUsersComponent);